import { Timestamp } from "firebase/firestore";
import type { Entity } from "../types";

/**
 * Whether an entity was a place you could go during a given stretch of time.
 *
 * The dates come from `effectiveActiveFrom` / `effectiveActiveTo`, which the
 * `onentitywrite` Cloud Function maintains by intersecting an entity's own
 * `activeFrom` / `activeTo` with every ancestor's — so retiring "Halloween
 * Horror Nights 2025" retires the eleven houses saved under it, and no client
 * has to walk `parentId` to find that out. What's left here is deliberately a
 * plain interval overlap: the Wear OS app carries the same rule, and anything
 * cleverer would be something for the two to disagree about.
 *
 * Null on either side of either interval means unbounded. Everything carries
 * null until somebody sets a date, so an undated entity is always active.
 */

/** A half-open-to-unbounded interval in epoch millis; null means no limit. */
export interface TimeWindow {
  fromMs: number | null;
  toMs: number | null;
}

/**
 * `activeTo` is the last day the place existed, not the first day it didn't.
 *
 * The forms store a bare date as local midnight (`EntityCreate`/`EntityDetail`
 * parse `"2025-11-02" + "T00:00:00"`), so comparing raw instants would retire
 * an event before its own closing night. The stored day runs through to the
 * end of that same day.
 */
function endOfDayMs(ts: Timestamp): number {
  const d = ts.toDate();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

type DatedEntity = Pick<Entity, "activeFrom" | "activeTo"> & Partial<Entity>;

/** The window to judge an entity by, preferring the inherited one. */
export function entityWindow(entity: DatedEntity): TimeWindow {
  // `effective*` is absent on entities written before the inheritance function
  // existed, and in the gap between a save and the trigger running. An
  // entity's own dates are the right answer for a root and a close-enough one
  // for a child until the backfill lands.
  const from =
    entity.effectiveActiveFrom !== undefined ? entity.effectiveActiveFrom : entity.activeFrom;
  const to = entity.effectiveActiveTo !== undefined ? entity.effectiveActiveTo : entity.activeTo;
  return {
    fromMs: from ? from.toMillis() : null,
    toMs: to ? endOfDayMs(to) : null,
  };
}

/** True when the two intervals share any instant. */
export function windowsOverlap(a: TimeWindow, b: TimeWindow): boolean {
  if (a.toMs != null && b.fromMs != null && a.toMs < b.fromMs) return false;
  if (a.fromMs != null && b.toMs != null && a.fromMs > b.toMs) return false;
  return true;
}

/**
 * Whether the entity existed at any point during `during`.
 *
 * Overlap rather than a point-in-time test because the question a trip asks is
 * "could I have gone here on this trip?" — a park week that starts before an
 * event's closing night should still offer that event. Erring open matters
 * more than erring closed here: hiding a place you actually visited makes it
 * unloggable, while showing one extra is a small annoyance.
 */
export function isEntityActiveDuring(entity: DatedEntity, during: TimeWindow): boolean {
  return windowsOverlap(entityWindow(entity), during);
}

/** Convenience for a single instant — the degenerate one-moment window. */
export function isEntityActiveOn(entity: DatedEntity, on: Date): boolean {
  const at = on.getTime();
  return isEntityActiveDuring(entity, { fromMs: at, toMs: at });
}

/** True when the entity carries any date limit at all — i.e. it can retire. */
export function hasActiveWindow(entity: DatedEntity): boolean {
  const { fromMs, toMs } = entityWindow(entity);
  return fromMs != null || toMs != null;
}

/**
 * Splits a list into what existed during `during` and what didn't.
 *
 * Both halves come back because the surfaces disagree about presentation, not
 * about the rule: the log flow shows only `active`, the places list shows
 * `retired` dimmed so you can still open and edit last year's houses, and the
 * search dropdown tucks `retired` into its own group.
 */
export function partitionByActive<T extends DatedEntity>(
  entities: T[],
  during: TimeWindow,
): { active: T[]; retired: T[] } {
  const active: T[] = [];
  const retired: T[] = [];
  for (const entity of entities) {
    (isEntityActiveDuring(entity, during) ? active : retired).push(entity);
  }
  return { active, retired };
}
